import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { Loader2 } from "lucide-react";
import { useTheme } from "./theme/ThemeContext";
import { auth } from "./config/firebase";
import Home from "./pages/Home";
import Login from "./pages/Login";
import SignupFlow from "./pages/SignupFlow";
import AuthAction from "./pages/AuthAction";
import PublicMasjids from "./pages/PublicMasjids";
import PublicEvents from "./pages/PublicEvents";
import DuasAndAyat from "./pages/DuasAndAyat";
import HalalInfo from "./pages/HalalInfo";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import PrayerTimes from "./pages/PrayerTimes";
import Events from "./pages/Events";
import Settings from "./pages/Settings";
import RestaurantDashboard from "./pages/HalalDashboard";
import HalalSignup from "./pages/HalalSignup";
import MasjidDashboard from "./pages/MasjidDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ApprovalManagement from "./pages/ApprovalManagement";
import HalalApprovalManagement from "./pages/HalalApprovalManagement";
import TeleView from "./pages/TeleView";
import PrayerTimesView from "./pages/PrayerTimesView";
import OverlayTest from "./pages/OverlayTest";

// Embeddable Widgets (in widgets folder)
import PrayerWidget from "./widgets/PrayerWidget";
import MonthlyPrayerWidget from "./widgets/MonthlyPrayerWidget";
import EventAgendaWidget from "./widgets/EventAgendaWidget";
import EventDayWidget from "./widgets/EventDayWidget";
import EventMonthWidget from "./widgets/EventMonthWidget";

// Widget Management Pages
import WidgetHub from "./pages/WidgetHub";
import WidgetPrayerPage from "./pages/WidgetPrayerPage";
import WidgetEventsPage from "./pages/WidgetEventsPage";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import MonthlyTimes from "./pages/MonthlyTimes";
import MasjidInfo from "./pages/MasjidInfo";
import MasjidSignup from "./pages/MasjidSignup";
import ContinueSignup from "./pages/ContinueSignup";
import PublicMasjidCalendar from "./pages/PublicMasjidCalendar";
import Solutions from "./pages/Solutions";
import SolutionsPortal from "./pages/SolutionsPortal";
import SolutionsTerms from "./pages/SolutionsTerms";
import RegistrationManagement from "./pages/RegistrationManagement";
import EventDetail from "./pages/EventDetail";

const getRoleHome = (userProfile) => {
  const role = userProfile?.role;

  if (role === "super") return "/admin";
  if (role === "halal") return "/halal";
  if (userProfile?.isAdmin) return "/masjid-dashboard";

  return "/";
};

// Loading Component
function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
        <h2 className="mb-2 text-2xl font-bold">Muslim+</h2>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [], adminOnly = false }) {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();

  const isMasjidAdmin =
    userProfile?.isAdmin === true || userProfile?.role === "masjid";

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    // Carry the intended destination so Login can return here after auth
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`}
        replace
      />
    );
  }

  // Check if route requires admin access
  if (adminOnly && !isMasjidAdmin) {
    return <Navigate to={getRoleHome(userProfile)} replace />;
  }

  // Check specific role requirements (for admin routes)
  if (allowedRoles.length > 0) {
    const userRole = userProfile?.role || "user";

    // Allow super admins to access everything
    if (userRole === "super") {
      return children;
    }

    // Check if user's role is in allowed roles
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to={getRoleHome(userProfile)} replace />;
    }
  }

  return children;
}

function AppRoutes() {
  const { user, userProfile } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      {import.meta.env.DEV && (
        <Route path="/overlay-test" element={<OverlayTest />} />
      )}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignupFlow />} />
      {/* Firebase auth email links land here. Must stay public: someone
          resetting a password is by definition not signed in. */}
      <Route path="/auth/action" element={<AuthAction />} />
      <Route path="/masjid-info" element={<MasjidInfo />} />
      <Route path="/masjid-signup" element={<MasjidSignup />} />
      <Route path="/continue-signup" element={<ContinueSignup />} />
      <Route path="/masjids" element={<PublicMasjids />} />
      <Route path="/masjids/calendar" element={<PublicMasjidCalendar />} />
      <Route path="/events-public" element={<PublicEvents />} />
      <Route path="/duas-ayat" element={<DuasAndAyat />} />
      <Route path="/halal-info" element={<HalalInfo />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/solutions/terms" element={<SolutionsTerms />} />
      <Route
        path="/solutions/portal"
        element={
          <ProtectedRoute>
            <SolutionsPortal />
          </ProtectedRoute>
        }
      />
      <Route path="/policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Event Share Link - Public page for shared events */}
      <Route path="/e/:shareCode" element={<EventDetail />} />
      
      {/* Event Share Link - Public page for shared events */}
      <Route path="/e/:shareCode" element={<EventDetail />} />
      
      {/* Widget Routes - Public embeddable widgets */}
      <Route path="/widget" element={<PrayerWidget />} />
      <Route path="/widget-monthly" element={<MonthlyPrayerWidget />} />
      <Route path="/widget-event-agenda" element={<EventAgendaWidget />} />
      <Route path="/widget-event-day" element={<EventDayWidget />} />
      <Route path="/widget-event-month" element={<EventMonthWidget />} />

      {/* Protected Routes - All Users */}
      <Route
        path="/dashboard"
        element={<Navigate to="/admin" replace />}
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes - All admin types can access */}
      <Route
        path="/prayer-times"
        element={
          <ProtectedRoute adminOnly={true}>
            <PrayerTimes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monthly-times"
        element={
          <ProtectedRoute adminOnly={true}>
            <MonthlyTimes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute adminOnly={true}>
            <Events />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId/registrations"
        element={
          <ProtectedRoute adminOnly={true}>
            <RegistrationManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/event/:eventId"
        element={<EventDetail />}
      />
      <Route
        path="/tv-viewer"
        element={
          <ProtectedRoute adminOnly={true}>
            <TeleView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tv-viewer"
        element={
          <ProtectedRoute adminOnly={true}>
            <TeleView />
          </ProtectedRoute>
        }
      />

      {/* Halal Business Admin Routes */}
      <Route
        path="/halal-signup"
        element={
          <ProtectedRoute>
            <HalalSignup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/halal"
        element={
          <ProtectedRoute allowedRoles={["halal"]}>
            <RestaurantDashboard />
          </ProtectedRoute>
        }
      />

      {/* Masjid Admin Routes */}
      <Route
        path="/masjid-dashboard"
        element={
          <ProtectedRoute adminOnly={true}>
            <MasjidDashboard />
          </ProtectedRoute>
        }
      />

      {/* Widget Management Routes - Protected */}
      <Route
        path="/widgets"
        element={
          <ProtectedRoute adminOnly={true}>
            <WidgetHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/widgets/prayer-times"
        element={
          <ProtectedRoute adminOnly={true}>
            <WidgetPrayerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/widgets/events"
        element={
          <ProtectedRoute adminOnly={true}>
            <WidgetEventsPage />
          </ProtectedRoute>
        }
      />

      {/* System Admin Routes - Super admin or Admin Owner */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prayer-times-view"
        element={
          <ProtectedRoute adminOnly={true}>
            <PrayerTimesView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/approvals"
        element={
          <ProtectedRoute allowedRoles={["super"]}>
            <ApprovalManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/halal-approvals"
        element={
          <ProtectedRoute allowedRoles={["super"]}>
            <HalalApprovalManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="*"
        element={
          <Navigate
            to={user ? getRoleHome(userProfile) : "/"}
            replace
          />
        }
      />
    </Routes>
  );
}

// Handles auth_token query param from mobile app deep-links
function TokenAuthHandler({ children }) {
  const [tokenLoading, setTokenLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("auth_token");
    if (!token) return;

    setTokenLoading(true);
    signInWithCustomToken(auth, token)
      .then(() => {
        // Strip the token from the URL and navigate to the intended path
        navigate(location.pathname, { replace: true });
      })
      .catch((err) => {
        console.error("Auto sign-in failed:", err);
        navigate("/login", { replace: true });
      })
      .finally(() => setTokenLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (tokenLoading) {
    return <LoadingScreen />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <TokenAuthHandler>
        <AppRoutes />
      </TokenAuthHandler>
    </AuthProvider>
  );
}

export default App;
